import type { OrderBuyerOutput, UpdateOrderInput } from "@/api/custom_types";
import { ChipInput } from "@/components";
import { useTranslation } from "@/hooks/useTranslation";
import { useUpdateOrder } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogScrollableArea,
	DialogTitle,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	Label,
	RadioGroup,
	RadioGroupItem,
	toast,
} from "@st-marketplace/ui";
import { t as translate } from "i18next";
import { useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

const common = z.object({
	email: z.string().email(),
	password: z.string().min(4),
});

const gauthVariant = z.object({
	auth_type: z.literal("gauth"),
	gauth: z.string().min(1),
	backup_code: z.array(z.string()).default([]),
});

const backupCodeVariant = z.object({
	auth_type: z.literal("backupCode"),
	backup_code: z
		.array(z.string())
		.min(1, translate("validation.backupCodeRequired")),
	gauth: z.string().optional(),
});

export const editOrderSchema = common.and(
	z.discriminatedUnion("auth_type", [gauthVariant, backupCodeVariant]),
);

export type EditOrderFormData = z.infer<typeof editOrderSchema>;
type AuthType = EditOrderFormData["auth_type"];

const INITIAL_FORM_DATA: EditOrderFormData = {
	auth_type: "gauth",
	email: "",
	password: "",
	gauth: "",
	backup_code: [],
};

const toFormData = (s?: OrderBuyerOutput): EditOrderFormData => {
	if (!s) return INITIAL_FORM_DATA;

	const base = {
		email: s.ea_email ?? "",
		password: s.ea_password ?? "",
	};

	switch (s.auth?.type) {
		case "gauth":
			return {
				...base,
				auth_type: "gauth",
				gauth: s.auth.auth,
				backup_code: [],
			};
		case "backup_code":
			return {
				...base,
				auth_type: "backupCode",
				gauth: "",
				backup_code: s.auth.auth,
			};
		case "cookie":
			return {
				...base,
				auth_type: "gauth",
				gauth: "",
				backup_code: [],
			};
		default:
			return INITIAL_FORM_DATA;
	}
};

const AuthTypeRadioGroup: React.FC<{
	value: AuthType;
	disabled?: boolean;
	onChange: (value: AuthType) => void;
}> = ({ value, onChange, disabled }) => {
	const { t } = useTranslation();

	return (
		<RadioGroup
			value={value}
			onValueChange={(v) => onChange(v as AuthType)}
			orientation="horizontal"
			className="flex flex-row gap-6"
			disabled={disabled}
		>
			<div className="flex items-center gap-2">
				<RadioGroupItem value="gauth" id="edit-order-gauth" />
				<Label htmlFor="edit-order-gauth">{t("editOrder.inputGauth")}</Label>
			</div>
			<div className="flex items-center gap-2">
				<RadioGroupItem value="backupCode" id="edit-order-backup-code" />
				<Label htmlFor="edit-order-backup-code">
					{t("editOrder.inputBackupCode")}
				</Label>
			</div>
		</RadioGroup>
	);
};

interface OrdersEditDialogProps {
	values?: OrderBuyerOutput;
	open: boolean;
	onClose: () => void;
}

const OrdersEditDialog: React.FC<OrdersEditDialogProps> = ({
	values,
	open,
	onClose,
}) => {
	const { t } = useTranslation();

	const updateOrderMutation = useUpdateOrder();

	const form = useForm<EditOrderFormData>({
		resolver: zodResolver(editOrderSchema),
		defaultValues: toFormData(values),
	});
	const { control, handleSubmit, reset, setValue } = form;

	useEffect(() => {
		reset(toFormData(values));
	}, [values, reset]);

	const currentAuthType = useWatch({ control, name: "auth_type" });
	useEffect(() => {
		if (currentAuthType === "gauth") {
			setValue("backup_code", [], { shouldValidate: true, shouldDirty: true });
		} else if (currentAuthType === "backupCode") {
			setValue("gauth", "", { shouldValidate: true, shouldDirty: true });
		}
	}, [currentAuthType, setValue]);

	const onSubmit = (data: EditOrderFormData) => {
		const payload: UpdateOrderInput = {
			ea_email: data.email,
			ea_password: data.password,
			auth:
				data.auth_type === "gauth"
					? { type: "gauth", auth: data.gauth }
					: { type: "backup_code", auth: data.backup_code },
		};

		updateOrderMutation.mutate(
			{ path: { order_id: values?.pk || "" }, body: payload },
			{
				onSuccess: () => {
					toast.success(t("editOrder.successMessage"));
					onClose();
				},
			},
		);
	};

	const handleReset = () => reset(toFormData(values));

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="w-full max-w-120">
				<DialogHeader>
					<DialogTitle>{t("orderInfo.tabInfo")}</DialogTitle>
					<DialogDescription>{t("orderInfo.title")}</DialogDescription>
				</DialogHeader>
				<DialogScrollableArea>
					<Form {...form}>
						<form
							noValidate
							onSubmit={handleSubmit(onSubmit)}
							className="flex flex-col gap-4"
						>
							<FormField
								control={control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>{t("editOrder.inputEmail")}</FormLabel>
										<FormControl>
											<Input
												{...field}
												ltr
												placeholder={t("editOrder.inputEmail")}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>
											{t("editOrder.inputPassword")}
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												ltr
												placeholder={t("editOrder.inputPassword")}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<Controller
								name="auth_type"
								control={control}
								render={({ field }) => (
									<AuthTypeRadioGroup
										value={field.value}
										onChange={(v) =>
											field.onChange(v as EditOrderFormData["auth_type"])
										}
									/>
								)}
							/>

							{currentAuthType === "gauth" ? (
								<FormField
									control={control}
									name="gauth"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t("editOrder.inputGauth")}</FormLabel>
											<FormControl>
												<Input
													{...field}
													value={field.value ?? ""}
													ltr
													placeholder={t("editOrder.inputGauth")}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							) : (
								<Controller
									name="backup_code"
									control={control}
									render={({ field }) => (
										<ChipInput
											chips={field.value ?? []}
											onChipsChange={(chips) => field.onChange(chips)}
											label={t("editOrder.inputBackupCode")}
										/>
									)}
								/>
							)}
						</form>
					</Form>
				</DialogScrollableArea>
				<DialogFooter
					buttons={{
						secondary: {
							label: t("editOrder.buttonReset"),
							props: { type: "button", onClick: handleReset },
						},
						primary: {
							label: t("editOrder.buttonSubmit"),
							props: {
								type: "button",
								loading: updateOrderMutation.isPending,
								onClick: () => void handleSubmit(onSubmit)(),
							},
						},
					}}
				/>
			</DialogContent>
		</Dialog>
	);
};

export default OrdersEditDialog;
